import { useRef, useEffect, memo } from 'react';
import * as d3 from 'd3';
import type { LineChartSeries } from '@/types/analytics';
import styles from './LineChart.module.scss';

export interface LineChartProps {
  series: LineChartSeries[];
  width: number;
  height: number;
  showDots?: boolean;
  showGrid?: boolean;
  animate?: boolean;
  yAxisLabel?: string;
  xAxisLabel?: string;
}

/**
 * Reusable D3 Line Chart Component
 *
 * Supports multiple series with smooth curves and animations.
 * Ideal for trend analysis and progression over time.
 *
 * @example
 * <LineChart
 *   series={[
 *     {
 *       name: 'Success Rate',
 *       data: [
 *         { x: '2024-01', y: 75 },
 *         { x: '2024-02', y: 82 },
 *         { x: '2024-03', y: 88 }
 *       ],
 *       color: 'var(--primary)'
 *     }
 *   ]}
 *   width={600}
 *   height={300}
 *   showDots
 *   showGrid
 * />
 */
export const LineChart = memo(function LineChart({
  series,
  width,
  height,
  showDots = true,
  showGrid = true,
  animate = true,
  yAxisLabel,
  xAxisLabel,
}: LineChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);

  // Memoize inner dimensions
  // const innerWidth = useMemo(() => width - margins.left - margins.right, [width, margins]);
  // const innerHeight = useMemo(() => height - margins.top - margins.bottom, [height, margins]);

  useEffect(() => {
    if (!svgRef.current || !series || series.length === 0 || width === 0 || height === 0) {
      return;
    }

    // Clear previous chart
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Chart margins
    const margin = { top: 20, right: 110, bottom: 60, left: 60 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    // Create chart group
    const chart = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Flatten all data points for scale domains
    const allData = series.flatMap((s) => s.data);

    // X Scale
    const xScale = d3
      .scalePoint()
      .domain(allData.map((d) => String(d.x)))
      .range([0, chartWidth])
      .padding(0.1);

    // Y Scale
    const yScale = d3
      .scaleLinear()
      .domain([0, d3.max(allData, (d) => d.y) || 100])
      .range([chartHeight, 0])
      .nice();

    // Grid lines
    if (showGrid) {
      // Horizontal grid
      chart
        .append('g')
        .attr('class', styles.lineChart__grid)
        .call(
          d3
            .axisLeft(yScale)
            .ticks(5)
            .tickSize(-chartWidth)
            .tickFormat(() => '')
        );
    }

    // X Axis
    const xDomain = xScale.domain();
    const maxTicks = width < 500 ? 5 : 8;
    const tickStep = Math.ceil(xDomain.length / maxTicks);
    const tickValues = xDomain.filter((_, i) => i % tickStep === 0);

    chart
      .append('g')
      .attr('class', styles.lineChart__axis)
      .attr('transform', `translate(0,${chartHeight})`)
      .call(d3.axisBottom(xScale).tickValues(tickValues))
      .selectAll('text')
      .style('text-anchor', 'middle');

    // Y Axis
    chart
      .append('g')
      .attr('class', styles.lineChart__axis)
      .call(d3.axisLeft(yScale).ticks(5));

    // Axis labels
    if (xAxisLabel) {
      svg
        .append('text')
        .attr('class', styles.lineChart__axisLabel)
        .attr('x', margin.left + chartWidth / 2)
        .attr('y', height - 10)
        .attr('text-anchor', 'middle')
        .text(xAxisLabel);
    }

    if (yAxisLabel) {
      svg
        .append('text')
        .attr('class', styles.lineChart__axisLabel)
        .attr('transform', 'rotate(-90)')
        .attr('x', -(margin.top + chartHeight / 2))
        .attr('y', 15)
        .attr('text-anchor', 'middle')
        .text(yAxisLabel);
    }

    // Line generator
    const lineGenerator = d3
      .line<{ x: string | number | Date; y: number }>()
      .x((d) => xScale(String(d.x)) || 0)
      .y((d) => yScale(d.y))
      .curve(d3.curveMonotoneX);

    // Draw lines for each series
    series.forEach((s, i) => {
      const path = chart
        .append('path')
        .datum(s.data)
        .attr('class', styles.lineChart__line)
        .attr('fill', 'none')
        .attr('stroke', s.color || `hsl(${(i * 360) / series.length}, 70%, 50%)`)
        .attr('stroke-width', 2)
        .attr('d', lineGenerator);

      if (animate) {
        const totalLength = path.node()?.getTotalLength() || 0;
        path
          .attr('stroke-dasharray', `${totalLength} ${totalLength}`)
          .attr('stroke-dashoffset', totalLength)
          .transition()
          .duration(1000)
          .ease(d3.easeCubicOut)
          .attr('stroke-dashoffset', 0);
      }

      // Draw dots
      if (showDots) {
        const dots = chart
          .selectAll(`.dot-${i}`)
          .data(s.data)
          .join('circle')
          .attr('class', styles.lineChart__dot)
          .attr('cx', (d) => xScale(String(d.x)) || 0)
          .attr('cy', (d) => yScale(d.y))
          .attr('r', 4)
          .attr('fill', s.color || `hsl(${(i * 360) / series.length}, 70%, 50%)`)
          .attr('stroke', 'var(--background)')
          .attr('stroke-width', 2);

        if (animate) {
          dots
            .attr('r', 0)
            .transition()
            .delay(800)
            .duration(200)
            .attr('r', 4);
        }
      }
    });

    // Interaction Layer & Tooltip
    // Cursor Line
    const cursorLine = chart.append('line')
      .attr('class', styles.lineChart__cursorLine)
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .style('opacity', 0);

    // Tooltip Group
    const tooltip = svg.append('g')
      .attr('class', styles.lineChart__tooltip)
      .style('opacity', 0);

    const tooltipBg = tooltip.append('rect')
      .attr('class', styles.lineChart__tooltipBg)
      .attr('rx', 4);

    const tooltipContent = tooltip.append('text')
      .attr('class', styles.lineChart__tooltipText);

    // Transparent Overlay for Events
    chart.append('rect')
      .attr('width', chartWidth)
      .attr('height', chartHeight)
      .attr('fill', 'transparent')
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event);
        const eachBand = xScale.step();
        const index = Math.round(mx / eachBand);
        const date = xDomain[index];

        if (!date) return;

        const xPos = xScale(date) || 0;

        // Update Cursor
        cursorLine
          .attr('x1', xPos)
          .attr('x2', xPos)
          .style('opacity', 1);

        // Update Tooltip
        tooltip.raise().style('opacity', 1);
        tooltipContent.selectAll('*').remove(); // Clear previous text

        // Formatted Date
        // Assume date string YYYY-MM-DD, try to format nicely
        let dateLabel = date;
        try {
          // Parse YYYY-MM-DD manually to avoid timezone issues with new Date(string)
          if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
            const [y, m, d] = date.split('-').map(Number);
            const dateObj = new Date(y, m - 1, d);
            dateLabel = d3.timeFormat('%b %d, %Y')(dateObj);
          } else {
            // Fallback for non-YYYY-MM-DD formats
            const d = new Date(date);
            if (!isNaN(d.getTime())) {
              dateLabel = d3.timeFormat('%b %d, %Y')(d);
            }
          }
        } catch (e) {
          // keep original
        }

        tooltipContent
          .append('tspan')
          .attr('x', 10)
          .attr('dy', '1.2em')
          .attr('class', styles['lineChart__tooltipText--date'])
          .text(dateLabel);

        // Series Values
        let dy = 1.2;
        series.forEach(s => {
          const point = s.data.find(d => String(d.x) === date);
          if (point) {
            dy += 1.2;
            const formattedValue = Number(point.y).toFixed(2);
            tooltipContent.append('tspan')
              .attr('x', 10)
              .attr('dy', '1.2em')
              .attr('fill', s.color || 'var(--text-primary)')
              .style('font-weight', 'bold')
              .text(`${s.name}: ${formattedValue}`);
            // Note: could add a small colored circle here too, but text color is simple
          }
        });

        // Resize Tooltip Background
        const bbox = tooltipContent.node()?.getBBox();
        if (bbox) {
          tooltipBg
            .attr('width', bbox.width + 20)
            .attr('height', bbox.height + 15);
        }

        // Position Tooltip
        // Prevent overflow
        let tx = margin.left + xPos + 15;
        let ty = margin.top;
        if (tx + (bbox?.width || 100) > width) {
          tx = margin.left + xPos - (bbox?.width || 100) - 15;
        }
        tooltip.attr('transform', `translate(${tx},${ty})`);

      })
      .on('mouseleave', () => {
        cursorLine.style('opacity', 0);
        tooltip.style('opacity', 0);
      });

    // Legend
    const legend = svg
      .append('g')
      .attr('class', styles.lineChart__legend)
      .attr('transform', `translate(${width - margin.right + 10},${margin.top})`);

    series.forEach((s, i) => {
      const legendRow = legend
        .append('g')
        .attr('transform', `translate(0,${i * 20})`);

      legendRow
        .append('rect')
        .attr('width', 12)
        .attr('height', 12)
        .attr('rx', 2)
        .attr('fill', s.color || `hsl(${(i * 360) / series.length}, 70%, 50%)`);

      legendRow
        .append('text')
        .attr('x', 18)
        .attr('y', 10)
        .attr('class', styles.lineChart__legendText)
        .text(s.name);
    });
  }, [series, width, height, showDots, showGrid, animate, yAxisLabel, xAxisLabel]);

  if (width === 0 || height === 0) {
    return null;
  }

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      className={styles.lineChart}
      role="img"
      aria-label="Line chart visualization"
    />
  );
});
